import React, { forwardRef, memo, useMemo } from "react";
import cx from "classnames";
import { LexicalComposer, type InitialConfigType } from "@lexical/react/LexicalComposer";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import {
  MarkdownConverterPlugin,
  convertFromMarkdown,
} from "./plugins/common/MarkdownConverterPlugin";
import { LexicalDefaultValueOnChangePlugin } from "./plugins/common/LexicalDefaultValueOnChangePlugin";
import { TargetBlankPlugin } from "./plugins/common/TargetBlankPlugin";
import { css } from "@linaria/core";
import { colors, fonts, spacings } from "../Theme";
import { MarkdownToolbar } from "./Toolbar/MarkdownToolbar";
import type { LexicalEditor } from "lexical";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { TableWrapperNode } from "./plugins/Table/nodes/TableWrapperNode";
import { DEFAULT_CONFIG_TABLE } from "./plugins/Table/utils/config";

export const DEFAULT_CONFIG = {
  namespace: "markdown-editor",
  nodes: [AutoLinkNode, LinkNode, TableNode, TableCellNode, TableRowNode, TableWrapperNode],
  theme: {
    paragraph: "paragraph-body1",
    text: { bold: "textBold", italic: "textItalic", placeholder: "placeholder" },
    tab: "tabNode",
    table: "table",
    tableAlignment: {
      center: "tableAlignmentCenter",
      right: "tableAlignmentRight",
    },
    tableCell: "tableCell",
    tableCellActionButton: "tableCellActionButton",
    tableCellActionButtonContainer: "tableCellActionButtonContainer",
    tableCellHeader: "tableCellHeader",
    tableCellResizer: "tableCellResizer",
    tableCellSelected: "tableCellSelected",
    tableRowStriping: "tableRowStriping",
    tableScrollableWrapper: "tableScrollableWrapper",
    tableSelected: "tableSelected",
    tableSelection: "tableSelection",
  },
  onError(e: Error) {
    throw e;
  },
};

function Markdown_(
  {
    config,
    defaultValue = "",
    placeholder = "",
    autoFocus = true,
    onConversion,
    error = false,
    editable = false,
    disabled = false,
    className,
    theme,
    children,
  }: {
    config?: Omit<InitialConfigType, "editable" | "namespace" | "onError">;
    defaultValue?: string;
    placeholder?: string;
    autoFocus?: boolean;
    onConversion?: (markdown: string) => void;
    error?: boolean;
    editable?: boolean;
    disabled?: boolean;
    className?: string;
    theme?: string;
    children?: (richTextEditor: React.ReactNode) => React.ReactNode;
  },
  ref: React.ForwardedRef<LexicalEditor | null>,
) {
  const initialConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...config,
      nodes: [...DEFAULT_CONFIG_TABLE.nodes, ...(config?.nodes ?? [])],
      theme: DEFAULT_CONFIG_TABLE.theme,
      editorState: config?.editorState ?? (() => convertFromMarkdown(defaultValue)),
      editable,
    }),
    [config, defaultValue, editable],
  );

  const richText = useMemo(
    () => (
      <div className={cx(richTextPlugin, defaultTheme, theme, { disabled, editable })}>
        <RichTextPlugin
          placeholder={editable ? <p className={richTextPlaceholder}>{placeholder}</p> : null}
          contentEditable={<ContentEditable readOnly={!editable} />}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
    ),
    [disabled, editable, placeholder, theme],
  );
  const editorRef = useEnsuredForwardedRef(ref);
  return (
    <>
      <div className={cx(markdownEditor, { editable, error }, className)}>
        <LexicalComposer initialConfig={initialConfig}>
          {typeof children === "function" && children?.(richText)}
          {!children && (
            <>
              {editable && <MarkdownToolbar />}
              {richText}
            </>
          )}
          {!editable && <LexicalDefaultValueOnChangePlugin value={defaultValue} />}
          {!editable && <TargetBlankPlugin />}
          {autoFocus && <AutoFocusPlugin />}
          <LinkPlugin />

          <ClearEditorPlugin />
          <EditorRefPlugin editorRef={editorRef} />
          {onConversion && <MarkdownConverterPlugin onConversion={onConversion} />}
        </LexicalComposer>
      </div>
    </>
  );
}

export const Markdown = memo(forwardRef(Markdown_));

//#region CSS
const markdownEditor = css`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-x: auto;

  &.disabled {
    pointer-events: none;
    background-color: ${colors.titanium50};
  }

  &.editable {
    border: 1px solid ${colors.titanium200};
    border-radius: 4px;
    background-color: ${colors.hydrogen};

    &:focus-within {
      border-color: ${colors.lightNitrogen500_Main};
    }
  }

  &.error.editable {
    border-color: ${colors.oxygen400};
  }
`;

const richTextPlugin = css`
  z-index: 0;
  display: flex;
  resize: none;
  overflow-y: auto;
  color: ${colors.carbon900};
  width: 100%;
  border: none;
  border-radius: 4px;
  text-align: left;
  text-overflow: clip;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  box-shadow: none !important;
  font: inherit;

  & > * {
    outline: none;
  }

  &[disabled] {
    background-color: ${colors.titanium50};
  }

  &::placeholder {
    color: ${colors.carbon500};
  }

  &.editable {
    padding: ${spacings.xs} ${spacings.s};
  }

  &:not(.editable) > :first-child {
    min-height: auto;
  }
`;

const richTextPlaceholder = css`
  position: relative;
  min-height: 34px;
  color: ${colors.carbon500};
  z-index: -1;
`;

const defaultTheme = css`
  position: relative;
  & > [contenteditable] {
    width: 100%;
    color: ${colors.carbon900};
    min-height: 34px;
    width: 100%;

    .tableScrollableWrapper {
      width: 100%;
      overflow-x: auto;
    }
    .tableScrollableWrapper > .table {
      margin-top: 0;
      margin-bottom: 0;
    }

    &:has(> p:only-child > br:only-child) {
      position: absolute;
      left: 0;
      text-indent: ${spacings.s};
      height: calc(100% - 2 * ${spacings.xs});
      width: 100%;
    }

    &:focus {
      outline: none;
    }

    &:focus + .placeholder {
      display: none;
    }

    .paragraph-body1 {
      font: ${fonts.body1};
    }
    .paragraph-body2 {
      font: ${fonts.body2};
    }

    .textBold {
      font-weight: bold;
    }

    .textItalic {
      font-style: italic;
    }

    strong {
      font: ${fonts.body1SemiBold};
    }

    a {
      color: ${colors.lightNitrogen500_Main};
    }

    & p:has(br:only-child):only-child + .placeholder {
      display: block;
      color: #aaa;
      position: absolute;
      top: 0;
      left: 10px;
      font-size: 16px;
    }

    & p:not(:has(br:only-child)) + .placeholder {
      display: none;
    }
  }

  & > [contenteditable="false"] {
    margin-top: 0;
  }
`;
//#endregion CSS
